import Grow from '@material-ui/core/Grow';
import Modal from '@material-ui/core/Modal';
import Paper from '@material-ui/core/Paper';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import classNames from 'classnames';
import deepmerge from 'deepmerge';
import React, { Dispatch, PureComponent } from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { Action } from 'redux';
import { SegmentResponse } from '../../models/itinerary';
import { AppState } from '../../store/model';
import { getSegmentType } from '../../store/reducers/itinerary/selectors';
import {
  selectSegment,
  cancelSegmentFromBackButton,
} from '../../store/reducers/selected-segment/actions';
import { isSelected as isSelectedSegment } from '../../store/reducers/selected-segment/selectors';
import { breakpointMobile } from '../lab/breakpoints';
import { spacings } from '../lab/spacings';
import { Content } from './Content/Content';
import { Overview } from './Overview';

interface OwnProps {
  segmentId: SegmentResponse['orderId'];
}

interface DispatchProps {
  onSegmentSelect(id: number): void;
}

interface StateProps {
  selected: boolean;
  segmentType: SegmentResponse['segmentType'];
}

interface MobileProps
  extends OwnProps,
    DispatchProps,
    StateProps,
    WithStyles<typeof styles> {}

class SegmentMobileBase extends PureComponent<MobileProps> {
  public render() {
    const { classes, selected, segmentId, segmentType } = this.props;
    return (
      <>
        <Overview segmentId={segmentId} onClick={this.onSegmentSelect} />
        <Modal
          id={`segment-${segmentId}`}
          open={selected}
          className={classes.modal}
        >
          <Grow in={selected}>
            <Paper
              square
              className={classNames(classes.content, {
                [classes[`type${segmentType}` as SegmentTypeClassKey]]: true,
              })}
            >
              <Content segmentId={segmentId} />
            </Paper>
          </Grow>
        </Modal>
      </>
    );
  }
  private onSegmentSelect = () => {
    const { onSegmentSelect, segmentId } = this.props;
    onSegmentSelect(segmentId);
  };
}

// styled
type SegmentTypeClassKey = 'typeAir' | 'typeCar' | 'typeHotel';

const styles = (theme: Theme) => {
  const {
    between: {
      card: { card: cardSpacing },
    },
  } = spacings(theme);

  const mobile = breakpointMobile(theme);

  return createStyles({
    container: deepmerge(cardSpacing, {
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      width: 'auto',
      height: 'auto',
      zIndex: 1,
      '&:first-of-type': {
        marginTop: 0,
      },
    }) as any,
    content: {
      flex: 'auto',
      flexDirection: 'row',
      overflow: 'hidden',
      overflowY: 'hidden',
      margin: 0,
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      maxHeight: 'none',
      [mobile]: {
        overflowY: 'scroll',
      },
    },
    modal: {
      maxHeight: '100vh',
    },
    typeAir: {
      backgroundColor: theme.zeno.travelModes.air.lightest,
    },
    typeCar: {
      backgroundColor: theme.zeno.travelModes.vehicle.lightest,
    },
    typeHotel: {
      backgroundColor: theme.zeno.travelModes.accommodation.lightest,
    },
  });
};

const mapStateToProps = (state: AppState, { segmentId }: OwnProps) => {
  return {
    selected: isSelectedSegment(String(segmentId))(state),
    segmentType: getSegmentType(String(segmentId))(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  onSegmentSelect: (id: string) => {
    dispatch(selectSegment(id));
    // We push a state to history so that we can capture the back button
    history.pushState(null, 'openModal', null);
    // We add an event listener to the back button to close the modal
    window.onpopstate = () => {
      dispatch(cancelSegmentFromBackButton());
    };
  },
});

const enhance = compose<MobileProps, OwnProps>(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStyles(styles),
);

/**
 * Segment component (for mobile screens)
 */
export const SegmentMobile = enhance(SegmentMobileBase);

SegmentMobile.displayName = 'SegmentMobile';
